import WebApp from "@twa-dev/sdk";

const BASE = import.meta.env.VITE_API_BASE_URL ?? "/v1";

interface SessionState {
  token: string | null;
  expiresAt: number;
}

const session: SessionState = { token: null, expiresAt: 0 };

/**
 * Authorization header strategy:
 *   - if we have a non-expired session token, send Bearer
 *   - otherwise, send raw initData as `tma <...>` and let the API mint a session
 *
 * The API returns the minted session via `X-Session-Token` so subsequent calls
 * skip the initData verification round-trip.
 */
function authHeader(): string {
  const nowSec = Math.floor(Date.now() / 1000);
  if (session.token && session.expiresAt - nowSec > 30) return `Bearer ${session.token}`;
  // In a non-Telegram environment (dev preview in browser), initData is empty.
  return `tma ${WebApp.initData ?? ""}`;
}

export class ApiError extends Error {
  constructor(public status: number, public code: string, message?: string) {
    super(message ?? code);
  }
}

export async function api<T>(path: string, init: RequestInit = {}): Promise<T> {
  const headers = new Headers(init.headers);
  headers.set("Authorization", authHeader());
  headers.set("Content-Type", "application/json");

  const res = await fetch(`${BASE}${path}`, { ...init, headers });

  const newToken = res.headers.get("X-Session-Token");
  const newExpires = res.headers.get("X-Session-Expires-At");
  if (newToken && newExpires) {
    session.token = newToken;
    session.expiresAt = Number(newExpires);
  }

  // Soft version banner — frontend.md / Phase 2 gate.
  const required = res.headers.get("X-App-Version-Required");
  if (required && import.meta.env.VITE_APP_VERSION && required !== import.meta.env.VITE_APP_VERSION) {
    window.dispatchEvent(new CustomEvent("app:stale-version", { detail: { required } }));
  }

  if (!res.ok) {
    let code = "request_failed";
    try {
      const body = (await res.json()) as { error?: string };
      if (body.error) code = body.error;
    } catch {
      /* ignore */
    }
    throw new ApiError(res.status, code);
  }

  return (await res.json()) as T;
}
